import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Remove,
  CompareArrows,
  Save,
  Delete,
  Search,
} from '@mui/icons-material';
import { FormatUtils } from '@mega-pokedex/shared';
import { useEnrichedCollection } from '../../hooks/useEnrichedCollection';
import { PokemonCard } from '../../types/pokemon';
import { TradeItem, CreateTradeInput } from '../../types/Trade';
import { tradeService } from '../../services/tradeService';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
// Services
import { cardSearchService, SearchResultCard } from '../../services/cardSearchService';
import { opCardSearchService } from '../../services/opCardSearchService';
import GameCard from '../../components/GameCard/GameCard';
import { useQuery } from '@tanstack/react-query';
// --- Card Selection Dialog ---
interface CardSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onSelect: (card: any) => void; // Using 'any' for now to support both Owned and searched cards
  mode: 'give' | 'get';
  ownedCards: any[]; // Enriched ownerships
  isAuthenticated: boolean;
}



const CardSelectionDialog: React.FC<CardSelectionDialogProps> = ({
  open,
  onClose,
  onSelect,
  mode,
  ownedCards,
  isAuthenticated,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchGlobal, setSearchGlobal] = useState(false);
  const [game, setGame] = useState<'pokemon' | 'onepiece'>('pokemon');

  // Debounced search term for TanStack Query
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm);
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: searchResultsData, isFetching: isSearching } = useQuery({
    queryKey: ['tradeSearch', game, debouncedTerm],
    queryFn: async () => {
      try {
        if (game === 'pokemon') {
          const data = await cardSearchService.searchCards(debouncedTerm);
          return data.cards;
        } else {
          const data = await opCardSearchService.filterCardsAdvanced({ searchTerm: debouncedTerm });
          return data.cards;
        }
      } catch (error) {
          console.error(error);
          return [];
      }
    },
    enabled: (searchGlobal || mode === 'get') && debouncedTerm.length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  const searchResults = searchResultsData || [];

  // Clean up state when opening/closing
  React.useEffect(() => {
      if (open) {
          setSearchTerm('');
          setSearchGlobal(mode === 'get' || !isAuthenticated); // Default to global for 'get' or guest users
      }
  }, [open, mode, isAuthenticated]);



  const displayCards = useMemo(() => {
    let source = [];
    if (searchGlobal || mode === 'get') {
        source = searchResults;
    } else {
        source = ownedCards || [];
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            source = source.filter((c) => {
                const name = c.cardName || c.name || '';
                return name.toLowerCase().includes(term);
            });
        }
        // Filter local search by game
        source = source.filter((c) => c.game === game);
    }
    return source;
  }, [mode, ownedCards, searchTerm, searchResults, searchGlobal, game]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{mode === 'give' ? 'Select Card to Trade' : 'Search Card to Receive'}</DialogTitle>
      <DialogContent>
         <Box sx={{ mb: 2, mt: 1, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={game}
              exclusive
              onChange={(_, newVal) => newVal && setGame(newVal)}
              size="small"
            >
              <ToggleButton value="pokemon">Pokemon</ToggleButton>
              <ToggleButton value="onepiece">One Piece</ToggleButton>
            </ToggleButtonGroup>
         </Box>
         <TextField 
            fullWidth 
            placeholder={!searchGlobal ? "Search your collection..." : "Search global database..."}
            value={searchTerm} 
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <Search /> }}
            sx={{ mb: 1, mt: 1 }}
         />
         
         {mode === 'give' && isAuthenticated && (
             <Box mb={1} display="flex" alignItems="center">
                 <FormControlLabel 
                    control={
                        <Switch 
                            checked={searchGlobal} 
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchGlobal(e.target.checked)} 
                            size="small"
                        />
                    }
                    label={<Typography variant="body2" color="textSecondary">Search Global Database (Include cards I don't own)</Typography>}
                 />
             </Box>
         )}
         
         {isSearching && <LinearProgress sx={{ mb: 1 }} />}

         <Box sx={{ maxHeight: 600, overflow: 'auto', p: 1 }}>
            {displayCards.length > 0 ? (
                <Grid container spacing={2}>
                    {displayCards.map((card: any, idx) => (
                        <Grid item xs={6} sm={4} md={3} key={card.id || card.uniqueId || card.cardId || idx}>
                            <GameCard 
                                card={card} 
                                game={game} 
                                layout="grid" 
                                onClick={() => onSelect(card)} 
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : (
                !isSearching && (
                    <Typography align="center" color="textSecondary" sx={{ mt: 2 }}>
                        {searchTerm ? 'No cards found.' : (searchGlobal ? 'Type to search...' : 'Start typing to search your collection.')}
                    </Typography>
                )
            )}
         </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};


const TradeCalculator: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { data: enrichedOwnerships = [] } = useEnrichedCollection();
    
    // Enrich ownerships for 'give' mode (searchable by name)
    const ownedCards = enrichedOwnerships.map(o => ({
        id: o.id,
        name: o.cardName,
        cardName: o.cardName, // Keep for backward compatibility in handleSelectCard
        setId: o.setId, // Expected by GameCard to lookup setName locally
        setName: o.setName,
        imageUrl: o.imageUrl, 
        images: { small: o.imageUrl }, // Expected by GameCard
        number: o.number, // Expected by GameCard
        currentValue: o.currentValue,
        game: o.game,
        type: 'give'
    }));

    const [giveItems, setGiveItems] = useState<TradeItem[]>([]);
    const [getItems, setGetItems] = useState<TradeItem[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'give' | 'get'>('give');

    // No longer blocking the whole page for non-authenticated users
    // if (!isAuthenticated && !process.env.IS_OFFLINE) {
    //     return (
    //         <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    //             <Alert severity="info">Please sign in to use the trade calculator.</Alert>
    //         </Box>
    //     );
    // }

    const handleAddItem = (mode: 'give' | 'get') => {
        setDialogMode(mode);
        setDialogOpen(true);
    };

    const handleSelectCard = (card: any) => {
        // Normalize card data from different sources (Ownership vs SearchResult vs OnePieceCard)
        const name = card.cardName || card.name;
        const image = card.imageUrl || card.images?.small;
        
        // Extract price based on type of card (enriched, One Piece, Pokemon)
        const opPrice = card.prices?.market || card.prices?.low;
        const pkmnPrice = card.tcgplayer?.prices?.holofoil?.market || card.tcgplayer?.prices?.normal?.market || card.tcgplayer?.prices?.reverseHolofoil?.market;
        const price = card.currentValue || opPrice || pkmnPrice || 10;

        const item: TradeItem = {
            cardId: card.id || card.cardId,
            name,
            setId: card.setId || card.set?.id,
            setName: card.setName || card.setId || card.set?.name,
            number: card.number,
            price,
            type: dialogMode,
            quantity: 1,
            image
        };

        if (dialogMode === 'give') {
            setGiveItems([...giveItems, item]);
        } else {
            setGetItems([...getItems, item]);
        }
        setDialogOpen(false);
    };

    const handleRemoveItem = (index: number, mode: 'give' | 'get') => {
        if (mode === 'give') {
            const newItems = [...giveItems];
            newItems.splice(index, 1);
            setGiveItems(newItems);
        } else {
            const newItems = [...getItems];
            newItems.splice(index, 1);
            setGetItems(newItems);
        }
    };

    const giveValue = giveItems.reduce((sum, item) => sum + item.price, 0);
    const getValue = getItems.reduce((sum, item) => sum + item.price, 0);
    const netValue = getValue - giveValue;
    
    // Fairness score: 
    // 50 = perfectly even
    // > 50 = you win
    // < 50 = you lose
    // Simple calc: (Get / (Get + Give)) * 100
    const totalVolume = giveValue + getValue;
    const fairnessScore = totalVolume === 0 ? 50 : (getValue / totalVolume) * 100;

    const handleSave = async () => {
        const trade: CreateTradeInput = {
            items: [...giveItems, ...getItems],
            netValue,
            fairnessScore
        };
        try {
            await tradeService.saveTrade(trade);
            navigate('/trades/history');
        } catch (error) {
            console.error(error);
            alert('Failed to save trade');
        }
    };

    return (
        <Box>
            <Typography variant="h4" gutterBottom fontWeight="bold">Trade Calculator</Typography>
            
            {/* Value Bar */}
            <Card sx={{ mb: 3, p: 2 }}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography fontWeight="bold" color="error.main">You Give: {FormatUtils.formatCurrency(giveValue)}</Typography>
                    <Typography fontWeight="bold" color={netValue >= 0 ? 'success.main' : 'error.main'}>
                        Net: {netValue > 0 ? '+' : ''}{FormatUtils.formatCurrency(netValue)}
                    </Typography>
                    <Typography fontWeight="bold" color="success.main">You Get: {FormatUtils.formatCurrency(getValue)}</Typography>
                </Box>
                <LinearProgress 
                    variant="determinate" 
                    value={fairnessScore} 
                    sx={{ 
                        height: 10, 
                        borderRadius: 5,
                        bgcolor: 'error.light',
                        '& .MuiLinearProgress-bar': { bgcolor: 'success.light' }
                    }} 
                />
                <Typography align="center" variant="caption" display="block" mt={0.5}>
                    Fairness Meter ({fairnessScore.toFixed(0)}%)
                </Typography>
            </Card>

            <Grid container spacing={2}>
                {/* Left: Give */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">You Give</Typography>
                                <Button startIcon={<Add />} onClick={() => handleAddItem('give')}>Add Card</Button>
                            </Box>
                            <List>
                                {giveItems.map((item, idx) => (
                                    <ListItem key={idx} secondaryAction={
                                        <IconButton edge="end" onClick={() => handleRemoveItem(idx, 'give')}>
                                            <Delete />
                                        </IconButton>
                                    }>
                                        <Box sx={{ width: 60, height: 84, mr: 2, flexShrink: 0, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
                                            {item.image ? (
                                                <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            ) : (
                                                <Typography variant="h5" color="text.secondary">{item.name[0]}</Typography>
                                            )}
                                        </Box>
                                        <ListItemText 
                                            primary={item.name} 
                                            secondary={
                                                <React.Fragment>
                                                    <Typography variant="body2" color="textPrimary">{FormatUtils.formatCurrency(item.price)}</Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {item.setName} {item.setId ? `[${item.setId}]` : ''} {item.number ? `#${item.number}` : ''}
                                                    </Typography>
                                                </React.Fragment>
                                            } 
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right: Get */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6">You Get</Typography>
                                <Button startIcon={<Add />} onClick={() => handleAddItem('get')}>Add Card</Button>
                            </Box>
                            <List>
                                {getItems.map((item, idx) => (
                                    <ListItem key={idx} secondaryAction={
                                        <IconButton edge="end" onClick={() => handleRemoveItem(idx, 'get')}>
                                            <Delete />
                                        </IconButton>
                                    }>
                                        <Box sx={{ width: 60, height: 84, mr: 2, flexShrink: 0, borderRadius: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.200' }}>
                                            {item.image ? (
                                                <img src={item.image} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            ) : (
                                                <Typography variant="h5" color="text.secondary">{item.name[0]}</Typography>
                                            )}
                                        </Box>
                                        <ListItemText 
                                            primary={item.name} 
                                            secondary={
                                                <React.Fragment>
                                                    <Typography variant="body2" color="textPrimary">{FormatUtils.formatCurrency(item.price)}</Typography>
                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                        {item.setName} {item.setId ? `[${item.setId}]` : ''} {item.number ? `#${item.number}` : ''}
                                                    </Typography>
                                                </React.Fragment>
                                            } 
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Box mt={3} textAlign="right">
                <Tooltip title={!isAuthenticated ? "Please sign in to save trades to your history" : ""}>
                    <span>
                        <Button 
                            variant="contained" 
                            size="large" 
                            startIcon={<Save />} 
                            onClick={handleSave}
                            disabled={!isAuthenticated}
                        >
                            Save Trade
                        </Button>
                    </span>
                </Tooltip>
            </Box>

            <CardSelectionDialog 
                open={dialogOpen} 
                onClose={() => setDialogOpen(false)} 
                mode={dialogMode}
                onSelect={handleSelectCard}
                ownedCards={ownedCards}
                isAuthenticated={isAuthenticated}
            />
        </Box>
    );
};

export default TradeCalculator;
